import React, { useEffect, useMemo, useState } from 'react'
import Select from 'react-select'
import * as API from '../../utility/api'
import moment from 'moment'

const ACTIONS = {
  SALES: 'sales',
  DEBT: 'debt'
}

const SALES_VIEWS = {
  EVENTS: 'events',
  SUMMARY: 'summary',
  MONTHS: 'months',
  SELLERS: 'sellers'
}

const SALES_VIEW_LABELS = {
  [SALES_VIEWS.EVENTS]: 'tapahtumittain',
  [SALES_VIEWS.SUMMARY]: 'yhteenveto',
  [SALES_VIEWS.MONTHS]: 'kuukausittain',
  [SALES_VIEWS.SELLERS]: 'myyjittäin'
}

const formatDateRange = (startDate, endDate) => {
  if (!startDate && !endDate) {
    return 'ei aikaväliä'
  }

  if (startDate && endDate) {
    const start = moment(startDate)
    const end = moment(endDate)
    if (
      start.isValid() &&
      end.isValid() &&
      start.isSame(end, 'month') &&
      start.date() === 1 &&
      end.isSame(start.clone().endOf('month'), 'day')
    ) {
      return start.format('MMMM YYYY')
    }

    return `${start.format('D.M.YYYY')}–${end.format('D.M.YYYY')}`
  }

  if (startDate) {
    return `${moment(startDate).format('D.M.YYYY')} alkaen`
  }

  return `${moment(endDate).format('D.M.YYYY')} asti`
}

const organizerLabel = organizer => `${organizer.name} (${organizer.id})`

const eventLabel = event => `${event.name} (${event.id})`

const sellerLabel = seller =>
  `${seller.name}${seller.email ? ` (${seller.email})` : ''} #${seller.id}`

const toSelectOption = (item, label) => ({
  value: String(item.id),
  label,
  item
})

const ReportFilterSelect = ({
  inputId,
  noOptionsMessage,
  isMulti = false,
  ...props
}) => (
  <Select
    inputId={inputId}
    className="raports-form-select"
    classNamePrefix="raports-form-select"
    isClearable
    isSearchable
    isMulti={isMulti}
    closeMenuOnSelect={!isMulti}
    hideSelectedOptions={false}
    placeholder="Hae..."
    noOptionsMessage={noOptionsMessage || (() => 'Ei osumia')}
    menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
    menuPosition="fixed"
    styles={{
      menuPortal: base => ({ ...base, zIndex: 40 })
    }}
    {...props}
  />
)

const Raports = ({
  startDate,
  endDate,
  onChangeDate,
  onClearDates,
  setRaportData
}) => {
  const [organizers, setOrganizers] = useState([])
  const [events, setEvents] = useState([])
  const [organizerID, setOrganizerID] = useState('')
  const [eventIds, setEventIds] = useState([])
  const [action, setAction] = useState(ACTIONS.SALES)
  const [salesView, setSalesView] = useState(SALES_VIEWS.EVENTS)
  const [includeOrganizer, setIncludeOrganizer] = useState(false)
  const [timeTarget, setTimeTarget] = useState('sold_at')
  const [loading, setLoading] = useState(false)
  const [filtersError, setFiltersError] = useState(null)
  const [eventsError, setEventsError] = useState(null)
  const [sellerID, setSellerID] = useState('')
  const [sellerQuery, setSellerQuery] = useState('')
  const [sellers, setSellers] = useState([])
  const [selectedSeller, setSelectedSeller] = useState(null)
  const [sellerError, setSellerError] = useState(null)
  const [scopeError, setScopeError] = useState(null)

  useEffect(() => {
    let cancelled = false

    API.GET('admin/reports/filters')
      .then(({ data }) => {
        if (cancelled) {
          return
        }
        setOrganizers(data.organizers || [])
        setFiltersError(null)
      })
      .catch(error => {
        if (cancelled) {
          return
        }
        console.error(error)
        setFiltersError('Järjestäjien lataus epäonnistui')
      })

    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    setEvents([])
    setEventIds([])
    setEventsError(null)

    if (!organizerID) {
      return
    }

    let cancelled = false

    API.GET('admin/reports/filters', { organizer_id: organizerID })
      .then(({ data }) => {
        if (cancelled) {
          return
        }
        setEvents(data.events || [])
        setEventsError(null)
      })
      .catch(error => {
        if (cancelled) {
          return
        }
        console.error(error)
        setEvents([])
        setEventIds([])
        setEventsError('Tapahtumien lataus epäonnistui')
      })

    return () => {
      cancelled = true
    }
  }, [organizerID])

  useEffect(() => {
    const query = sellerQuery.trim()
    const isId = /^[1-9]\d*$/.test(query)
    if (!isId && query.length < 2) {
      setSellers([])
      setSellerError(null)
      return
    }

    let cancelled = false
    const handle = setTimeout(() => {
      API.GET('admin/reports/filters', { seller_q: query })
        .then(({ data }) => {
          if (cancelled) {
            return
          }
          setSellers(data.sellers || [])
          setSellerError(null)
        })
        .catch(error => {
          if (cancelled) {
            return
          }
          console.error(error)
          setSellers([])
          setSellerError('Myyjien haku epäonnistui')
        })
    }, 300)

    return () => {
      cancelled = true
      clearTimeout(handle)
    }
  }, [sellerQuery])

  const organizerOptions = useMemo(
    () =>
      organizers.map(organizer =>
        toSelectOption(organizer, organizerLabel(organizer))
      ),
    [organizers]
  )

  const eventOptions = useMemo(
    () => events.map(event => toSelectOption(event, eventLabel(event))),
    [events]
  )

  const sellerOptions = useMemo(() => {
    const options = selectedSeller
      ? [toSelectOption(selectedSeller, sellerLabel(selectedSeller))]
      : []
    for (const seller of sellers) {
      if (!options.some(option => option.value === String(seller.id))) {
        options.push(toSelectOption(seller, sellerLabel(seller)))
      }
    }
    return options
  }, [selectedSeller, sellers])

  const selectedOrganizerOption =
    organizerOptions.find(option => option.value === String(organizerID)) ||
    null
  const selectedEventOptions = eventOptions.filter(option =>
    eventIds.some(eventId => String(eventId) === option.value)
  )
  const selectedSellerOption =
    sellerOptions.find(option => option.value === String(sellerID)) || null

  const selectedOrganizer = organizers.find(
    organizer => String(organizer.id) === String(organizerID)
  )
  const isSales = action === ACTIONS.SALES
  const hasEntityFilter =
    Boolean(organizerID) ||
    eventIds.length > 0 ||
    (isSales && Boolean(sellerID))
  const hasDateRange = Boolean(startDate && endDate)
  const hasInvalidDateRange =
    hasDateRange && moment(startDate).isAfter(moment(endDate), 'day')

  const selectionSummary = useMemo(() => {
    const parts = [isSales ? 'Myynti' : 'Velka']

    if (isSales) {
      parts.push(SALES_VIEW_LABELS[salesView] || salesView)
    }

    parts.push(formatDateRange(startDate, endDate))

    if (selectedOrganizer) {
      parts.push(selectedOrganizer.name)
    } else {
      parts.push('kaikki järjestäjät')
    }

    if (eventIds.length === 1) {
      const event = events.find(
        item => String(item.id) === String(eventIds[0])
      )
      parts.push(event ? event.name : '1 tapahtuma')
    } else if (eventIds.length > 1) {
      parts.push(`${eventIds.length} tapahtumaa`)
    }

    if (isSales && selectedSeller) {
      parts.push(`myyjä ${selectedSeller.name} #${selectedSeller.id}`)
    }

    return parts.join(', ')
  }, [
    isSales,
    salesView,
    startDate,
    endDate,
    selectedOrganizer,
    eventIds,
    events,
    selectedSeller
  ])

  const fetchData = e => {
    e.preventDefault()

    if (hasInvalidDateRange) {
      setScopeError('Alkupäivä ei voi olla loppupäivän jälkeen.')
      return
    }

    if (!hasDateRange && !hasEntityFilter) {
      setScopeError(
        'Valitse aikaväli tai rajaa raportti järjestäjään, tapahtumaan tai myyjään.'
      )
      return
    }

    setScopeError(null)
    setLoading(true)
    const startTime = startDate
      ? moment(new Date(`${startDate} 00:00:00`))
          .utc()
          .format('YYYY-MM-DD HH:mm:ss')
      : undefined
    const endTime = endDate
      ? moment(new Date(`${endDate} 23:59:59`))
          .utc()
          .format('YYYY-MM-DD HH:mm:ss')
      : undefined

    API.POST(`admin/reports/${action}`, {
      start_time: startTime,
      end_time: endTime,
      organizer_id: organizerID || undefined,
      event_ids: eventIds,
      seller_id: isSales ? sellerID || undefined : undefined,
      time_target: timeTarget,
      view: isSales ? salesView : undefined,
      include_organizer: includeOrganizer
    })
      .then(({ data }) => {
        setLoading(false)
        setRaportData(data)
      })
      .catch(error => {
        setLoading(false)
        console.error(error)
        if (error?.response?.status === 400) {
          setScopeError(
            'Valitse aikaväli tai rajaa raportti järjestäjään, tapahtumaan tai myyjään.'
          )
        }
      })
  }

  return (
    <form className="admin-panel--raports--form-container" onSubmit={fetchData}>
      <div className="admin-panel--raports--form raports-form-layout">
        <fieldset className="raports-form-card">
          <legend>Mitä</legend>

          <div className="raports-form-field">
            <span className="raports-form-label">Raportin tyyppi</span>
            <div className="raports-form-options">
              <label htmlFor="sales">
                <input
                  id="sales"
                  className="raport-radio"
                  type="radio"
                  checked={action === ACTIONS.SALES}
                  onChange={() => setAction(ACTIONS.SALES)}
                />
                <span>Myynti</span>
              </label>
              <label htmlFor="debt">
                <input
                  id="debt"
                  className="raport-radio"
                  type="radio"
                  checked={action === ACTIONS.DEBT}
                  onChange={() => setAction(ACTIONS.DEBT)}
                />
                <span>Velka (sis. alv)</span>
              </label>
            </div>
          </div>

          {isSales && (
            <>
              <div className="raports-form-field">
                <span className="raports-form-label">Näkymä</span>
                <div className="raports-form-options">
                  <label htmlFor="view-events">
                    <input
                      id="view-events"
                      className="raport-radio"
                      type="radio"
                      checked={salesView === SALES_VIEWS.EVENTS}
                      onChange={() => setSalesView(SALES_VIEWS.EVENTS)}
                    />
                    <span>Tapahtumittain</span>
                  </label>
                  <label htmlFor="view-summary">
                    <input
                      id="view-summary"
                      className="raport-radio"
                      type="radio"
                      checked={salesView === SALES_VIEWS.SUMMARY}
                      onChange={() => setSalesView(SALES_VIEWS.SUMMARY)}
                    />
                    <span>Vain yhteenveto</span>
                  </label>
                  <label htmlFor="view-months">
                    <input
                      id="view-months"
                      className="raport-radio"
                      type="radio"
                      checked={salesView === SALES_VIEWS.MONTHS}
                      onChange={() => setSalesView(SALES_VIEWS.MONTHS)}
                    />
                    <span>Kuukausittain</span>
                  </label>
                  <label htmlFor="view-sellers">
                    <input
                      id="view-sellers"
                      className="raport-radio"
                      type="radio"
                      checked={salesView === SALES_VIEWS.SELLERS}
                      onChange={() => setSalesView(SALES_VIEWS.SELLERS)}
                    />
                    <span>Myyjittäin</span>
                  </label>
                </div>
              </div>

              <div className="raports-form-field">
                <div className="raports-form-options">
                  <label htmlFor="includeOrganizer">
                    <input
                      id="includeOrganizer"
                      className="raport-radio"
                      type="checkbox"
                      checked={includeOrganizer}
                      onChange={e => setIncludeOrganizer(e.target.checked)}
                    />
                    <span>Näytä järjestäjätason erittely</span>
                  </label>
                </div>
              </div>
            </>
          )}
        </fieldset>

        <fieldset className="raports-form-card">
          <legend>Miltä ajalta ja keneltä</legend>

          <div className="raports-form-field">
            <span className="raports-form-label">Aikaväli</span>
            <div className="raports-form-dates">
              <label htmlFor="report-start-date">
                Alkaen
                <input
                  id="report-start-date"
                  type="date"
                  value={startDate}
                  max={endDate || undefined}
                  onChange={e => onChangeDate(e.target.value, 'startDate')}
                />
              </label>
              <label htmlFor="report-end-date">
                Päättyy
                <input
                  id="report-end-date"
                  type="date"
                  value={endDate}
                  min={startDate || undefined}
                  onChange={e => onChangeDate(e.target.value, 'endDate')}
                />
              </label>
              <button type="button" onClick={onClearDates}>
                Tyhjennä aikaväli
              </button>
            </div>
          </div>

          <div className="raports-form-field">
            <span className="raports-form-label">Ajankohdan kohdistus</span>
            <div className="raports-form-options">
              <label htmlFor="sold_at">
                <input
                  id="sold_at"
                  className="raport-radio"
                  type="radio"
                  checked={timeTarget === 'sold_at'}
                  onChange={() => setTimeTarget('sold_at')}
                />
                <span>Lipun myyntiaika</span>
              </label>
              <label htmlFor="paytrail_payed_at">
                <input
                  id="paytrail_payed_at"
                  className="raport-radio"
                  type="radio"
                  checked={timeTarget === 'paytrail_payed_at'}
                  onChange={() => setTimeTarget('paytrail_payed_at')}
                />
                <span>Tilauksen maksuaika</span>
              </label>
            </div>
          </div>

          <div className="raports-form-field">
            <label htmlFor="organizerID">Järjestäjä</label>
            <ReportFilterSelect
              inputId="organizerID"
              options={organizerOptions}
              value={selectedOrganizerOption}
              placeholder="Kaikki järjestäjät"
              onChange={option => {
                setEvents([])
                setEventIds([])
                setEventsError(null)
                setOrganizerID(option ? option.value : '')
              }}
            />
            {filtersError && (
              <p className="raports-form-hint">{filtersError}</p>
            )}
          </div>

          <div className="raports-form-field">
            <label htmlFor="eventIds">Tapahtumat</label>
            {organizerID ? (
              <>
                <ReportFilterSelect
                  inputId="eventIds"
                  isMulti
                  options={eventOptions}
                  value={selectedEventOptions}
                  placeholder="Kaikki tapahtumat"
                  noOptionsMessage={() => eventsError || 'Ei osumia'}
                  onChange={options =>
                    setEventIds(
                      (options || []).map(option => Number(option.value))
                    )
                  }
                />
                {eventsError && (
                  <p className="raports-form-hint">{eventsError}</p>
                )}
              </>
            ) : (
              <p className="raports-form-hint">
                Valitse järjestäjä, jos haluat rajata tapahtumiin.
              </p>
            )}
          </div>

          {isSales && (
            <div className="raports-form-field">
              <label htmlFor="sellerID">Myyjä</label>
              <ReportFilterSelect
                inputId="sellerID"
                options={sellerOptions}
                value={selectedSellerOption}
                placeholder="Kaikki myyjät"
                noOptionsMessage={() => {
                  const query = sellerQuery.trim()
                  if (sellerError) {
                    return sellerError
                  }
                  if (!query) {
                    return 'Kirjoita nimi, sähköposti tai ID'
                  }
                  if (!/^[1-9]\d*$/.test(query) && query.length < 2) {
                    return 'Kirjoita vähintään 2 merkkiä'
                  }
                  return 'Ei osumia'
                }}
                onInputChange={(value, { action: inputAction }) => {
                  if (inputAction === 'input-change') {
                    setSellerQuery(value)
                  }
                }}
                onChange={option => {
                  setSellerID(option ? option.value : '')
                  setSelectedSeller(option ? option.item : null)
                  setSellerQuery('')
                }}
              />
              {sellerError && (
                <p className="raports-form-hint">{sellerError}</p>
              )}
            </div>
          )}
        </fieldset>

        <div className="raports-form-actions">
          <p className="raports-form-summary">{selectionSummary}</p>
          <p className="raports-form-hint">
            {hasEntityFilter
              ? 'Aikaväli on valinnainen, kun järjestäjä, tapahtuma tai myyjä on valittu.'
              : 'Ilman järjestäjä-, tapahtuma- tai myyjärajausta tarvitaan sekä alku- että loppupäivä.'}
          </p>
          {scopeError && (
            <p className="raports-form-hint" role="alert">
              {scopeError}
            </p>
          )}
          <button disabled={loading} type="submit">
            {loading ? 'Ladataan....' : 'Lataa raportti'}
          </button>
        </div>
      </div>
    </form>
  )
}

export default Raports
